/*
  FP Collection Compare v17
  Progressive dropdown compare flow for collection pages.

  Required markup/data:
  - [data-fp-compare-toggle], [data-fp-compare], [data-fp-compare-view],
    [data-fp-compare-wrap], [data-fp-compare-clear], [data-fp-compare-print], [data-fp-compare-close]
  - .fp-dietary-item with product data attrs and child script[data-fp-nutrition-json]
  - script[data-fp-competitors-json] for competitor meals
*/
(function () {
  'use strict';

  var NUTRITION_ROWS = [
    { key: 'energy_kcal', label: 'Energy (kcal)', unit: '', decimals: 0 },
    { key: 'fat_g', label: 'Fat', unit: 'g', decimals: 1 },
    { key: 'saturates_g', label: 'of which saturates', unit: 'g', decimals: 1, sub: true },
    { key: 'carbohydrate_g', label: 'Carbohydrates', unit: 'g', decimals: 1 },
    { key: 'sugars_g', label: 'of which sugars', unit: 'g', decimals: 1, sub: true },
    { key: 'fibre_g', label: 'Fibre', unit: 'g', decimals: 1 },
    { key: 'protein_g', label: 'Protein', unit: 'g', decimals: 1 },
    { key: 'salt_g', label: 'Salt', unit: 'g', decimals: 2 }
  ];

  var VIEW_ROWS = {
    nutrition_regular: NUTRITION_ROWS,
    nutrition_xl: NUTRITION_ROWS,
    nutrition_100g: NUTRITION_ROWS,
    water: [
      { key: 'weight_regular', label: 'Regular meal weight', unit: 'g', decimals: 0 },
      { key: 'water_regular', label: 'Regular meal water', unit: 'ml', decimals: 0 },
      { key: 'rehydrated_regular', label: 'Rehydrated regular meal weight', unit: 'g', decimals: 0 },
      { key: 'weight_xl', label: 'Extra Large meal weight', unit: 'g', decimals: 0 },
      { key: 'water_xl', label: 'Extra Large meal water', unit: 'ml', decimals: 0 },
      { key: 'rehydrated_xl', label: 'Rehydrated extra large meal weight', unit: 'g', decimals: 0 }
    ],
    weight: [
      { key: 'weight_regular', label: 'Pack weight (Regular)', unit: 'g', decimals: 0 },
      { key: 'weight_xl', label: 'Pack weight (Extra Large)', unit: 'g', decimals: 0 }
    ],
    ingredients: [
      { key: 'ingredients', label: 'Ingredients', text: true }
    ],
    allergens: [
      { key: 'is_vegan', label: 'Vegan', tick: true },
      { key: 'is_gluten_free', label: 'Gluten free', tick: true },
      { key: 'is_lactose_free', label: 'Lactose free', tick: true },
      { key: 'allergens', label: 'Allergens', text: true }
    ]
  };

  function ready(fn) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', fn);
    else fn();
  }

  function parseJSONNode(node, fallback) {
    if (!node) return fallback;
    try { return JSON.parse(node.textContent || ''); } catch (e) { return fallback; }
  }

  function asNumber(value) {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return isFinite(value) ? value : null;
    var n = Number(String(value).replace(/[^\d.\-]/g, ''));
    return isFinite(n) ? n : null;
  }

  function valueFromField(field) {
    if (field === null || field === undefined) return '';
    if (Array.isArray(field)) {
      return field.map(valueFromField).filter(Boolean).join(', ');
    }
    if (typeof field === 'object') {
      if (field.value !== undefined) return valueFromField(field.value);
      if (field.name !== undefined) return field.name;
      if (field.title !== undefined) return field.title;
      if (field.label !== undefined) return field.label;
      return '';
    }
    return String(field);
  }

  function formatValue(value, row) {
    if (row && row.tick) {
      return value ? '✓' : '';
    }

    if (row && row.text) {
      var text = valueFromField(value).trim();
      return text || '—';
    }

    var n = asNumber(value);
    if (n === null) return '—';

    var decimals = row && typeof row.decimals === 'number' ? row.decimals : 1;
    var out = n.toFixed(decimals);
    out = out.replace(/\.0$/, '');
    return out + (row && row.unit ? row.unit : '');
  }

  function normaliseListText(value) {
    return valueFromField(value).toLowerCase();
  }

  function hasTag(tags, needle) {
    needle = String(needle || '').toLowerCase();
    return (tags || []).some(function (tag) {
      return String(tag || '').toLowerCase().trim() === needle;
    });
  }

  function containsAny(text, needles) {
    text = String(text || '').toLowerCase();
    return needles.some(function (needle) { return text.indexOf(needle) !== -1; });
  }

  function getNutritionValue(meal, key, mode) {
    var n = meal && meal.nutrition ? meal.nutrition : {};
    var entry = n[key] || (key === 'carbohydrate_g' ? n.carbs_g : null);
    if (!entry) return null;

    if (typeof entry === 'object' && !Array.isArray(entry)) {
      if (mode === 'per_100g') return entry.per_100g;
      return entry[mode];
    }

    return entry;
  }

  function getMealValue(meal, view, row) {
    if (!meal) return null;

    if (view === 'nutrition_regular') return getNutritionValue(meal, row.key, 'regular');
    if (view === 'nutrition_xl') return getNutritionValue(meal, row.key, 'xl');
    if (view === 'nutrition_100g') return getNutritionValue(meal, row.key, 'per_100g');

    var regularWeight = meal.weights && asNumber(meal.weights.regular_g || meal.weights.regular);
    var xlWeight = meal.weights && asNumber(meal.weights.xl_g || meal.weights.xl);
    var regularWater = meal.water && asNumber(meal.water.regular_ml || meal.water.regular);
    var xlWater = meal.water && asNumber(meal.water.xl_ml || meal.water.xl);

    if (row.key === 'water_regular') return regularWater;
    if (row.key === 'water_xl') return xlWater;
    if (row.key === 'weight_regular') return regularWeight;
    if (row.key === 'weight_xl') return xlWeight;
    if (row.key === 'rehydrated_regular') return regularWeight !== null && regularWater !== null ? regularWeight + regularWater : null;
    if (row.key === 'rehydrated_xl') return xlWeight !== null && xlWater !== null ? xlWeight + xlWater : null;
    if (row.key === 'ingredients') return meal.ingredients;
    if (row.key === 'allergens') return meal.allergens;
    if (row.key === 'is_vegan') return !!meal.isVegan;
    if (row.key === 'is_gluten_free') return !!meal.isGlutenFree;
    if (row.key === 'is_lactose_free') return !!meal.isLactoseFree;

    return null;
  }

  function normaliseFirepotMeal(el) {
    var json = parseJSONNode(el.querySelector('script[data-fp-nutrition-json]'), {});
    var handle = el.getAttribute('data-fp-handle') || '';
    var title = el.getAttribute('data-fp-title') || handle;
    var tags = String(el.getAttribute('data-fp-tags') || '').split(',').map(function (tag) { return tag.trim().toLowerCase(); }).filter(Boolean);
    var allergensText = normaliseListText(json.allergens || '');
    return {
      uid: 'fp__' + handle,
      type: 'firepot',
      handle: handle,
      title: title,
      sortTitle: title.toLowerCase(),
      url: el.getAttribute('data-fp-url') || '',
      image: el.getAttribute('data-fp-img') || '',
      weights: json.weights || {},
      water: json.water || {},
      ingredients: json.ingredients || '',
      allergens: json.allergens || '',
      isVegan: hasTag(tags, 'vegan'),
      isGlutenFree: hasTag(tags, 'gluten free') || hasTag(tags, 'gluten-free') || hasTag(tags, 'glutenfree'),
      isLactoseFree: !containsAny(allergensText, ['milk', 'lactose', 'dairy']),
      nutrition: json.nutrition || {}
    };
  }

  function normaliseCompetitorMeal(raw) {
    raw = raw || {};
    var handle = raw.handle || ('cmp__' + (raw.comparison_id || raw.id || raw.product_name || Math.random()));
    var title = raw.title || [raw.brand, raw.product_name].filter(Boolean).join(' — ') || raw.product_name || raw.comparison_id || handle;
    return {
      uid: String(handle).indexOf('cmp__') === 0 ? String(handle) : ('cmp__' + String(handle)),
      type: 'competitor',
      handle: String(handle),
      title: title,
      sortTitle: String(title).toLowerCase(),
      url: '',
      image: raw.image || raw.image_url || raw.product_image || raw.pouch_image || 'https://cdn.shopify.com/s/files/1/1596/8375/files/sample-pouch.png?v=1777884768',
      weights: raw.weights || { regular_g: raw.pouch_weight_g || null, xl_g: null },
      water: {
        regular_ml: raw.water_to_add_ml || (raw.water && raw.water.regular_ml) || null,
        xl_ml: raw.water_to_add_ml || (raw.water && raw.water.xl_ml) || null
      },
      ingredients: raw.ingredients || '',
      allergens: raw.allergens || '',
      isVegan: !!(raw.is_vegan || raw.vegan || raw.isVegan),
      isGlutenFree: !!(raw.is_gluten_free || raw.gluten_free || raw.glutenFree || raw.isGlutenFree),
      isLactoseFree: raw.is_lactose_free !== undefined ? !!raw.is_lactose_free : !containsAny(normaliseListText(raw.allergens || ''), ['milk', 'lactose', 'dairy']),
      nutrition: raw.nutrition || {
        energy_kcal: { per_100g: raw.kcal_100g, regular: raw.kcal_pouch, xl: raw.kcal_pouch },
        fat_g: { per_100g: raw.fat_100g, regular: raw.fat_pouch, xl: raw.fat_pouch },
        saturates_g: { per_100g: raw.of_which_saturates_100g, regular: raw.of_which_saturates_pouch, xl: raw.of_which_saturates_pouch },
        carbohydrate_g: { per_100g: raw.carbohydrate_100g, regular: raw.carbohydrate_pouch, xl: raw.carbohydrate_pouch },
        sugars_g: { per_100g: raw.of_which_sugars_100g, regular: raw.of_which_sugars_pouch, xl: raw.of_which_sugars_pouch },
        fibre_g: { per_100g: raw.fibre_100g, regular: raw.fibre_pouch, xl: raw.fibre_pouch },
        protein_g: { per_100g: raw.protein_100g, regular: raw.protein_pouch, xl: raw.protein_pouch },
        salt_g: { per_100g: raw.salt_100g, regular: raw.salt_pouch, xl: raw.salt_pouch }
      }
    };
  }

  function optionHTML(meal, selectedMap) {
    var selected = !!selectedMap[meal.uid];
    var prefix = selected ? '✓ ' : '';
    return '<option value="' + escapeHTML(meal.uid) + '"' + (selected ? ' disabled' : '') + '>' +
      escapeHTML(prefix + meal.title) +
      '</option>';
  }

  function escapeHTML(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function isMobileCompare() {
    return window.matchMedia && window.matchMedia('(max-width: 699px)').matches;
  }

  ready(function () {
    document.querySelectorAll('.shopify-section--main-collection').forEach(function (sectionRoot) {
      var compareRoot = sectionRoot.querySelector('[data-fp-compare]');
      if (!compareRoot) return;

      var toggle = sectionRoot.querySelector('[data-fp-compare-toggle]');
      var viewSelect = compareRoot.querySelector('[data-fp-compare-view]');
      var wrap = compareRoot.querySelector('[data-fp-compare-wrap]');
      var hint = compareRoot.querySelector('[data-fp-compare-hint]');
      var clearBtn = compareRoot.querySelector('[data-fp-compare-clear]');
      var printBtn = compareRoot.querySelector('[data-fp-compare-print]');
      var closeBtn = compareRoot.querySelector('[data-fp-compare-close]');
      var titleNode = compareRoot.querySelector('.fp-compare__title');
      if (titleNode) titleNode.textContent = 'Compare';

      var viewContainer = compareRoot.querySelector('.fp-compare__view');

      var limitMsg = compareRoot.querySelector('[data-fp-compare-limit-msg]');
      if (!limitMsg) {
        limitMsg = document.createElement('span');
        limitMsg.className = 'fp-compare__limitMsg';
        limitMsg.setAttribute('data-fp-compare-limit-msg', '');
        limitMsg.hidden = true;

        var actionsNode = compareRoot.querySelector('.fp-compare__actions');
        if (actionsNode && actionsNode.parentNode) {
          actionsNode.parentNode.insertBefore(limitMsg, actionsNode);
        } else if (compareRoot.querySelector('.fp-compare__top')) {
          compareRoot.querySelector('.fp-compare__top').appendChild(limitMsg);
        }
      }

      var firepotMeals = Array.prototype.slice.call(sectionRoot.querySelectorAll('.fp-dietary-item'))
        .filter(function (el) { return el.getAttribute('data-fp-compare-disabled') !== 'true'; })
        .map(normaliseFirepotMeal)
        .sort(function (a, b) { return a.sortTitle.localeCompare(b.sortTitle); });

      var competitorMeals = parseJSONNode(compareRoot.querySelector('script[data-fp-competitors-json]'), [])
        .map(normaliseCompetitorMeal)
        .sort(function (a, b) { return a.sortTitle.localeCompare(b.sortTitle); });

      var mealMap = {};
      firepotMeals.concat(competitorMeals).forEach(function (meal) { mealMap[meal.uid] = meal; });

      var selected = [];
      var maxDesktop = 6;
      var maxMobile = 2;

      function maxCols() {
        return isMobileCompare() ? maxMobile : maxDesktop;
      }

      function openCompare() {
        compareRoot.hidden = false;
        document.body.setAttribute('data-fp-compare-active', 'true');
        if (toggle) toggle.setAttribute('aria-expanded', 'true');
        render();
        compareRoot.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }

      function closeCompare() {
        compareRoot.hidden = true;
        document.body.removeAttribute('data-fp-compare-active');
        if (toggle) toggle.setAttribute('aria-expanded', 'false');
      }

      function clearCompare() {
        selected = [];
        render();
      }

      function addMeal(uid) {
        if (!uid || !mealMap[uid]) return;
        if (selected.indexOf(uid) !== -1) return;
        if (selected.length >= maxCols()) return;
        selected.push(uid);
        render();
      }

      function removeMeal(uid) {
        selected = selected.filter(function (x) { return x !== uid; });
        render();
      }

      function selectedMap() {
        var m = {};
        selected.forEach(function (uid) { m[uid] = true; });
        return m;
      }

      function buildPickerColumn() {
        var map = selectedMap();
        var hasCompetitors = competitorMeals && competitorMeals.length > 0;
        var competitorLabel = isMobileCompare() ? '...or a competitor' : 'Choose competitor';

        var firepotOptions = '<option value="">Choose a Firepot</option>' +
          firepotMeals.map(function (meal) { return optionHTML(meal, map); }).join('');

        var firepotSelect =
          '<select class="fp-compare__mealSelect" data-fp-select-kind="firepot" aria-label="Choose a Firepot">' +
          firepotOptions +
          '</select>';

        if (!hasCompetitors) {
          return '<div class="fp-compare__picker fp-compare__picker--firepotOnly" data-fp-picker>' +
            firepotSelect +
            '</div>';
        }

        var competitorOptions = '<option value="">' + escapeHTML(competitorLabel) + '</option>' +
          competitorMeals.map(function (meal) { return optionHTML(meal, map); }).join('');

        return '<div class="fp-compare__picker" data-fp-picker>' +
          firepotSelect +
          '<select class="fp-compare__mealSelect" data-fp-select-kind="competitor" aria-label="' + escapeHTML(competitorLabel) + '">' + competitorOptions + '</select>' +
          '</div>';
      }

      function buildHeaderCell(uid, index) {
        var meal = mealMap[uid];
        if (!meal) return '';

        var img = meal.image
          ? '<img class="fp-compare__mealImg" src="' + escapeHTML(meal.image) + '" alt="" loading="lazy">'
          : '<div class="fp-compare__mealImg fp-compare__mealImg--empty" aria-hidden="true"></div>';

        return '<div class="fp-compare__mealHead">' +
          '<button type="button" class="fp-compare__remove" data-fp-remove="' + escapeHTML(uid) + '" aria-label="Remove ' + escapeHTML(meal.title) + '">×</button>' +
          '<div class="fp-compare__mealTitle">' + escapeHTML(meal.title) + '</div>' +
          img +
          '</div>';
      }

      function render() {
        var max = maxCols();

        if (selected.length > max) {
          selected = selected.slice(0, max);
        }

        var view = viewSelect ? viewSelect.value : 'nutrition_regular';
        var rows = VIEW_ROWS[view] || VIEW_ROWS.nutrition_regular;
        var columns = selected.slice();
        var canPick = columns.length < max;
        var fixedCols = max;
        var rowNumber = 1;

        compareRoot.setAttribute('data-fp-selected-count', String(selected.length));
        compareRoot.setAttribute('data-fp-max-cols', String(max));
        compareRoot.setAttribute('data-fp-can-pick', canPick ? 'true' : 'false');

        if (limitMsg) {
          if (selected.length >= max && !isMobileCompare()) {
            limitMsg.textContent = 'You can compare up to six products. Remove one before adding another.';
            limitMsg.hidden = false;
          } else {
            limitMsg.textContent = '';
            limitMsg.hidden = true;
          }
        }

        var selectedText = viewSelect && viewSelect.options && viewSelect.selectedIndex >= 0
          ? viewSelect.options[viewSelect.selectedIndex].text
          : 'Compare';

        if (viewContainer) {
          viewContainer.setAttribute('data-fp-print-title', selectedText);
        }

        var html = '<div class="fp-compare__table" style="--fp-compare-cols:' + fixedCols + '">';

        html += '<div class="fp-compare__cell fp-compare__cell--corner" style="grid-column:1;grid-row:' + rowNumber + '">';
        html += '<span class="fp-compare__mobileViewLabel">' + escapeHTML(selectedText) + '</span>';
        html += '</div>';

        columns.forEach(function (uid, c) {
          html += '<div class="fp-compare__cell fp-compare__cell--head" style="grid-column:' + (c + 2) + ';grid-row:' + rowNumber + '">';
          html += buildHeaderCell(uid, c);
          html += '</div>';
        });

        if (canPick) {
          html += '<div class="fp-compare__cell fp-compare__cell--head fp-compare__cell--pickerHead" style="grid-column:' + (columns.length + 2) + ';grid-row:' + rowNumber + '">';
          html += buildPickerColumn();
          html += '</div>';
        }

        for (var hc = columns.length + (canPick ? 1 : 0); hc < fixedCols; hc++) {
          html += '<div class="fp-compare__cell fp-compare__cell--head fp-compare__cell--emptyHead" style="grid-column:' + (hc + 2) + ';grid-row:' + rowNumber + '"></div>';
        }

        rows.forEach(function (row, r) {
          var gridRow = r + 2;
          var rowClass = (r % 2 === 0) ? ' fp-compare__cell--alt' : '';
          html += '<div class="fp-compare__cell fp-compare__cell--label' + rowClass + (row.sub ? ' fp-compare__cell--sub' : '') + '" style="grid-column:1;grid-row:' + gridRow + '">' + escapeHTML(row.label) + '</div>';

          for (var i = 0; i < fixedCols; i++) {
            var uid = columns[i];
            var meal = uid ? mealMap[uid] : null;
            var val = meal ? getMealValue(meal, view, row) : null;
            html += '<div class="fp-compare__cell fp-compare__cell--value' + rowClass + (meal ? '' : ' fp-compare__cell--emptyValue') + (row.text ? ' fp-compare__cell--text' : '') + (row.tick ? ' fp-compare__cell--tick' : '') + '" style="grid-column:' + (i + 2) + ';grid-row:' + gridRow + '">' +
              (meal ? escapeHTML(formatValue(val, row)) : '') +
              '</div>';
          }
        });

        html += '</div>';
        wrap.innerHTML = html;

        if (hint) {
          if (selected.length === 0) hint.textContent = isMobileCompare() ? 'Choose two meals to compare.' : 'Choose up to six meals to compare.';
          else if (selected.length >= max) hint.textContent = isMobileCompare() ? 'Mobile comparison is limited to two meals. Remove one to choose another.' : 'Six meals selected. Remove one to choose another.';
          else hint.textContent = 'Choose another meal from the next column, or remove a selected meal.';
        }
      }

      if (toggle) {
        toggle.addEventListener('click', function () {
          if (compareRoot.hidden) openCompare();
          else closeCompare();
        });
      }

      if (viewSelect) viewSelect.addEventListener('change', render);
      if (clearBtn) clearBtn.addEventListener('click', clearCompare);
      if (closeBtn) closeBtn.addEventListener('click', closeCompare);
      function removePrintClone() {
        var oldClone = document.getElementById('fp-compare-print-clone');
        if (oldClone && oldClone.parentNode) oldClone.parentNode.removeChild(oldClone);
        document.body.classList.remove('fp-compare-printing');
      }

      if (printBtn) printBtn.addEventListener('click', function () {
        render();
        removePrintClone();

        var printClone = compareRoot.cloneNode(true);
        printClone.id = 'fp-compare-print-clone';
        printClone.classList.add('fp-compare--printClone');
        printClone.hidden = false;
        printClone.removeAttribute('data-fp-compare');

        document.body.appendChild(printClone);
        document.body.classList.add('fp-compare-printing');

        window.setTimeout(function () { window.print(); }, 80);
      });

      window.addEventListener('afterprint', removePrintClone);

      window.addEventListener('focus', function () {
        window.setTimeout(removePrintClone, 300);
      });

      compareRoot.addEventListener('change', function (event) {
        var target = event.target;
        if (!target || !target.matches || !target.matches('select[data-fp-select-kind]')) return;
        addMeal(target.value);
      });

      compareRoot.addEventListener('click', function (event) {
        var btn = event.target && event.target.closest ? event.target.closest('[data-fp-remove]') : null;
        if (!btn) return;
        removeMeal(btn.getAttribute('data-fp-remove'));
      });

      window.addEventListener('resize', function () {
        window.clearTimeout(compareRoot.__fpCompareResizeTimer);
        compareRoot.__fpCompareResizeTimer = window.setTimeout(render, 120);
      });
    });
  });
})();
